import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { LanguageProvider } from '../../core/language-provider';

const CUSTOMER_ORDER_STATUSES = [
    { value: '0_PENDING', variant: 'primary' },
    { value: '2_TO_ORDER', variant: 'dark' },
    { value: '1_ORDERED', variant: 'secondary' },
    { value: '7_READY_FOR_DELIVERY', variant: 'info' },
    { value: '15_PARTIAL_DELIVERED', variant: 'warning' },
    { value: '10_COMPLETED', variant: 'success' }
];

const ENTITY_STATUS_CONFIG = {
    customer_order: {
        resetVariant: 'outline-secondary',
        statuses: CUSTOMER_ORDER_STATUSES
    },
    gyv_customer_order: {
        resetVariant: 'outline-secondary',
        statuses: CUSTOMER_ORDER_STATUSES
    },
    customer_offer: {
        resetVariant: 'outline-secondary',
        statuses: [
            { value: '0_PENDING', variant: 'primary' },
            { value: '10_CONFIRMED', variant: 'success' },
            { value: '20_DECLINED', variant: 'danger' }
        ]
    },
    gyv_customer_offer: {
        resetVariant: 'outline-secondary',
        statuses: [
            { value: '0_PENDING', variant: 'primary' },
            { value: '10_CONFIRMED', variant: 'success' },
            { value: '20_DECLINED', variant: 'danger' }
        ]
    },
    gyv_customer_order_delivery: {
        resetVariant: 'outline-secondary',
        statuses: [
            { value: '10_CREATED', variant: 'primary' },
            { value: '20_DELIVERED', variant: 'success' }
        ]
    },
    supplier_order: {
        resetVariant: 'outline-secondary',
        statuses: [
            { value: '1_TO_SEND', variant: 'secondary' },
            { value: '2_SENT', variant: 'primary' },
            { value: '5_PARTIAL', variant: 'warning' },
            { value: '10_COMPLETED', variant: 'success' }
        ]
    },
    gyv_supplier_order: {
        resetVariant: 'outline-secondary',
        statuses: [
            { value: '1_TO_SEND', variant: 'secondary' },
            { value: '2_SENT', variant: 'primary' },
            { value: '5_PARTIAL', variant: 'warning' },
            { value: '10_COMPLETED', variant: 'success' }
        ]
    }
};


export default function DataGridTopHook({ entity, data, setData, searchObj, setSearchObj }) {

    const [activeStatus, setActiveStatus] = useState(null);

    const statusConfig = ENTITY_STATUS_CONFIG[entity];

    const statusButtons = useMemo(() => {
        if (!statusConfig) {
            return [];
        }

        return statusConfig.statuses.map(({ value, variant }) => ({
            value,
            variant,
            label: LanguageProvider.getLabel(entity, `status.options.${value}`) || value
        }));
    }, [entity, statusConfig]);

    useEffect(() => {
        if (!statusConfig) {
            return;
        }

        const constraints = (searchObj && Array.isArray(searchObj.constraints)) ? searchObj.constraints : [];
        const statusConstraint = constraints.find((item) => item.fieldName === 'status' && item.type === 'value');
        const nextActive = statusConstraint ? statusConstraint.value : null;

        setActiveStatus((prev) => (prev !== nextActive ? nextActive : prev));
    }, [entity, searchObj, statusConfig]);

    const handleStatusClick = (statusValue) => {
        if (typeof setSearchObj !== 'function') {
            return;
        }

        const baseSearchObj = searchObj || { query: '', fields: [], constraints: [] };
        const currentConstraints = Array.isArray(baseSearchObj.constraints) ? baseSearchObj.constraints : [];
        const constraintsWithoutStatus = currentConstraints.filter((item) => item.fieldName !== 'status');

        const nextStatus = statusValue === activeStatus ? null : statusValue;

        const nextConstraints = nextStatus
            ? [...constraintsWithoutStatus, { fieldName: 'status', type: 'value', value: nextStatus }]
            : constraintsWithoutStatus;

        const nextSearchObj = {
            ...baseSearchObj,
            constraints: nextConstraints
        };

        setActiveStatus(nextStatus);
        setSearchObj(nextSearchObj);
    };

    const resetLabel = LanguageProvider.get('defaults.reset') || 'Tutti';

    const renderStatusButtons = () => {
        if (!statusConfig) {
            return <></>;
        }

        if (!statusButtons.length) {
            return <></>;
        }

        const resetVariant = statusConfig.resetVariant || 'primary';

        return (
            <div className='mb-3 d-flex flex-wrap align-items-center gap-2'>
                <Button
                    size='sm'
                    variant={activeStatus === null ? resetVariant : `outline-${resetVariant}`}
                    onClick={() => handleStatusClick(null)}
                    active={activeStatus === null}
                >
                    {resetLabel}
                </Button>
                {statusButtons.map((status) => {
                    const isActive = activeStatus === status.value;
                    const baseVariant = status.variant || 'secondary';
                    const variant = isActive ? baseVariant : `outline-${baseVariant}`;

                    return (
                        <Button
                            key={status.value}
                            size='sm'
                            variant={variant}
                            onClick={() => handleStatusClick(status.value)}
                            active={isActive}
                        >
                            {status.label}
                        </Button>
                    );
                })}
            </div>
        );
    };


    if (entity !== null) {
        if (statusConfig) {
            return renderStatusButtons();
        }

        return (
            <>
                {(() => {
                    switch(entity) {
                        case 'sample':
                            return (
                                <span>Test</span>
                            );
                        default:
                            return (
                                <></>
                            );
                    }
                })()}
            </>
        )
    }
    else {
        return (
            <></>
        )
    }
};
